<template>
  <div>
    <label
      :for="disabled ? undefined : id"
      class="mb-1 block text-xs text-gray-600"
    >
      {{ name }}
      <sup
        v-if="required"
        class="top-0 text-sm font-bold"
        :class="disabled ? 'text-gray-600' : 'text-red-400'"
      >
        *
      </sup>
    </label>
    <input
      :id="id"
      v-model.trim="model"
      :type="type"
      :disabled="disabled"
      :placeholder="placeholder"
      :maxlength="maxlength"
      :class="className"
      class="block w-full rounded border border-solid border-gray-600 bg-gray-100 px-2 py-[5px] text-sm focus:border-blue-400"
      @focus="handleFocus"
      @blur="handleBlur"
      @input="handleChange"
    />
    <span v-show="isError" class="mt-1 block px-2 text-xs text-red-400">
      {{ errorMessage }}
    </span>
  </div>
</template>

<script setup lang="ts">
  import { v4 as uuidv4 } from 'uuid';
  import type { InputHTMLAttributes } from 'vue';

  interface IProps {
    id: string;
    name: string;
    type?: InputHTMLAttributes['type'];
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
    maxlength?: number;
    isError?: boolean;
    errorMessage?: string;
  }

  const props = withDefaults(defineProps<IProps>(), {
    id: uuidv4(),
    name: '',
    type: 'text',
    required: false,
    disabled: false,
    placeholder: '',
    maxlength: 100,
    isError: false,
    errorMessage: '',
  });

  const emits = defineEmits<{
    (e: 'blur', value: string): void;
    (e: 'change', value: string): void;
  }>();

  const model = defineModel<string>({ default: '' });

  const isFocus = ref<boolean>(false);

  const className = computed((): string => {
    if (props.disabled) {
      return 'pointer-events-none cursor-not-allowed opacity-40';
    }
    if (props.isError) {
      return 'border-red-400';
    }
    return '';
  });

  const handleFocus = (): void => {
    isFocus.value = true;
  };

  const handleBlur = (): void => {
    isFocus.value = false;
    emits('blur', model.value);
  };

  const handleChange = (): void => {
    emits('change', model.value);
  };
</script>
